import React, { useState } from "react";

const tokens = {
  colors: [
    { name: "Navy", hex: "#1B2B4B", text: "white" },
    { name: "Coral", hex: "#E8623A", text: "white" },
    { name: "Sage", hex: "#7BA69A", text: "white" },
    { name: "Cream", hex: "#F7F3EE", text: "#9B9590", border: true },
    { name: "Warm Gray", hex: "#9B9590", text: "white" },
  ],
  spacing: [4, 8, 12, 16, 24, 32, 48, 64],
};

function DesignSystem() {
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);

  return (
    <div
      style={{
        fontFamily: "'DM Sans', sans-serif",
        background: "#F7F3EE",
        minHeight: "100vh",
        color: "#1A1A1A",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Syne:wght@700;800&display=swap');
        .ds-btn { border: none; cursor: pointer; font-family: 'DM Sans', sans-serif; font-weight: 500; border-radius: 8px; transition: all 0.2s; display: inline-flex; align-items: center; }
        .ds-btn:hover { transform: translateY(-2px); }
        .ds-card { border-radius: 12px; overflow: hidden; transition: all 0.25s; cursor: default; }
        .ds-card:hover { transform: translateY(-4px); }
        .ds-swatch { border-radius: 8px; aspect-ratio: 1; cursor: pointer; position: relative; transition: transform 0.2s; }
        .ds-swatch:hover { transform: scale(1.06); }
        .section-line::after { content: ''; flex: 1; height: 1px; background: #E8E4DF; }
      `}</style>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "48px 32px" }}>
        {/* HEADER */}
        <div
          style={{
            marginBottom: 64,
            borderBottom: "1px solid #E8E4DF",
            paddingBottom: 40,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#E8623A",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            Helin Okcu — Design System
          </div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: "clamp(36px, 6vw, 64px)",
              fontWeight: 800,
              color: "#1B2B4B",
              lineHeight: 1.05,
              marginBottom: 16,
            }}
          >
            Components
            <br />& Tokens
          </h1>
          <p style={{ color: "#6B6560", fontSize: 16, maxWidth: 480 }}>
            A living reference for the visual language, components, and patterns
            used across my projects.
          </p>
          <a
            href="/"
            style={{
              display: "inline-flex",
              marginTop: 24,
              fontSize: 13,
              color: "#9B9590",
              textDecoration: "none",
              gap: 6,
              alignItems: "center",
            }}
          >
            ← Back to portfolio
          </a>
        </div>

        {/* COLORS */}
        <div style={{ marginBottom: 64 }}>
          <div
            className="section-line"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#9B9590",
              fontWeight: 600,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            Colour Palette
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 12,
            }}
          >
            {tokens.colors.map((c) => (
              <div
                key={c.name}
                className="ds-swatch"
                style={{
                  background: c.hex,
                  border: c.border ? "1px solid #E8E4DF" : "none",
                }}
                onMouseEnter={() => setHoveredColor(c.name)}
                onMouseLeave={() => setHoveredColor(null)}
              >
                <span
                  style={{
                    position: "absolute",
                    bottom: 8,
                    left: 8,
                    fontSize: 10,
                    fontWeight: 600,
                    color: c.text,
                    letterSpacing: "0.05em",
                  }}
                >
                  {c.name}
                </span>
                <span
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    fontSize: 9,
                    color: c.text,
                    fontFamily: "monospace",
                    opacity: hoveredColor === c.name ? 1 : 0,
                    transition: "opacity 0.2s",
                  }}
                >
                  {c.hex}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* TYPOGRAPHY */}
        <div style={{ marginBottom: 64 }}>
          <div
            className="section-line"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#9B9590",
              fontWeight: 600,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            Typography
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {[
              {
                meta: "Display\nSyne 800\n48px",
                el: (
                  <span
                    style={{
                      fontFamily: "Syne, sans-serif",
                      fontSize: 48,
                      fontWeight: 800,
                      color: "#1B2B4B",
                      lineHeight: 1,
                    }}
                  >
                    Design
                  </span>
                ),
              },
              {
                meta: "H1\nSyne 700\n32px",
                el: (
                  <span
                    style={{
                      fontFamily: "Syne, sans-serif",
                      fontSize: 32,
                      fontWeight: 700,
                      color: "#1B2B4B",
                    }}
                  >
                    Heading One
                  </span>
                ),
              },
              {
                meta: "H2\nSyne 700\n22px",
                el: (
                  <span
                    style={{
                      fontFamily: "Syne, sans-serif",
                      fontSize: 22,
                      fontWeight: 700,
                      color: "#1B2B4B",
                    }}
                  >
                    Heading Two
                  </span>
                ),
              },
              {
                meta: "Body\nDM Sans 400\n16px",
                el: (
                  <span style={{ fontSize: 16 }}>
                    The quick brown fox jumps over the lazy dog.
                  </span>
                ),
              },
              {
                meta: "Small\nDM Sans 400\n13px",
                el: (
                  <span style={{ fontSize: 13, color: "#6B6560" }}>
                    Supporting text and helper copy lives here.
                  </span>
                ),
              },
              {
                meta: "Caption\nDM Sans 600\n11px",
                el: (
                  <span
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase" as const,
                      color: "#9B9590",
                    }}
                  >
                    Label / Caption / Eyebrow
                  </span>
                ),
              },
            ].map((row, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 24,
                  padding: "16px 0",
                  borderBottom: "1px solid #E8E4DF",
                }}
              >
                <span
                  style={{
                    width: 80,
                    fontSize: 10,
                    color: "#9B9590",
                    fontFamily: "monospace",
                    flexShrink: 0,
                    whiteSpace: "pre-line",
                    lineHeight: 1.6,
                  }}
                >
                  {row.meta}
                </span>
                {row.el}
              </div>
            ))}
          </div>
        </div>

        {/* BUTTONS */}
        <div style={{ marginBottom: 64 }}>
          <div
            className="section-line"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#9B9590",
              fontWeight: 600,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            Buttons
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            <button
              className="ds-btn"
              style={{
                background: "#1B2B4B",
                color: "white",
                padding: "12px 24px",
                fontSize: 14,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(27,43,75,0.25)")
              }
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
            >
              Primary
            </button>
            <button
              className="ds-btn"
              style={{
                background: "#E8623A",
                color: "white",
                padding: "12px 24px",
                fontSize: 14,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(232,98,58,0.3)")
              }
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
            >
              Coral
            </button>
            <button
              className="ds-btn"
              style={{
                background: "transparent",
                color: "#1B2B4B",
                padding: "11px 23px",
                fontSize: 14,
                border: "1.5px solid #1B2B4B",
              }}
            >
              Outline
            </button>
            <button
              className="ds-btn"
              style={{
                background: "transparent",
                color: "#9B9590",
                padding: "12px 24px",
                fontSize: 14,
              }}
            >
              Ghost
            </button>
            <button
              className="ds-btn"
              style={{
                background: "#1B2B4B",
                color: "white",
                padding: "8px 16px",
                fontSize: 12,
              }}
            >
              Small
            </button>
            <button
              className="ds-btn"
              style={{
                background: "#1B2B4B",
                color: "white",
                padding: "16px 32px",
                fontSize: 16,
              }}
            >
              Large
            </button>
            <button
              className="ds-btn"
              style={{
                background: "#E8E4DF",
                color: "#1A1A1A",
                width: 40,
                height: 40,
                justifyContent: "center",
                borderRadius: 8,
                fontSize: 16,
              }}
            >
              ↗
            </button>
          </div>
        </div>

        {/* INPUTS */}
        <div style={{ marginBottom: 64 }}>
          <div
            className="section-line"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#9B9590",
              fontWeight: 600,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            Inputs
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              maxWidth: 400,
            }}
          >
            {[
              {
                id: "default",
                label: "Default",
                placeholder: "Enter your email",
                hint: "We'll never share your email.",
                error: false,
                disabled: false,
              },
              {
                id: "error",
                label: "Error state",
                placeholder: "",
                defaultValue: "invalid@",
                hint: "Please enter a valid email address.",
                error: true,
                disabled: false,
              },
              {
                id: "disabled",
                label: "Disabled",
                placeholder: "Not available",
                hint: "",
                error: false,
                disabled: true,
              },
            ].map((input) => (
              <div
                key={input.id}
                style={{ display: "flex", flexDirection: "column", gap: 6 }}
              >
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  {input.label}
                </label>
                <input
                  disabled={input.disabled}
                  defaultValue={input.defaultValue}
                  placeholder={input.placeholder}
                  onFocus={() => setFocusedInput(input.id)}
                  onBlur={() => setFocusedInput(null)}
                  style={{
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: 14,
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: `1.5px solid ${input.error ? "#E8623A" : focusedInput === input.id ? "#1B2B4B" : "#E8E4DF"}`,
                    background: input.disabled ? "#E8E4DF" : "white",
                    color: input.disabled ? "#9B9590" : "#1A1A1A",
                    outline: "none",
                    width: "100%",
                    boxShadow:
                      focusedInput === input.id
                        ? `0 0 0 3px ${input.error ? "rgba(232,98,58,0.1)" : "rgba(27,43,75,0.1)"}`
                        : "none",
                    transition: "all 0.2s",
                    cursor: input.disabled ? "not-allowed" : "text",
                  }}
                />
                {input.hint && (
                  <span
                    style={{
                      fontSize: 11,
                      color: input.error ? "#E8623A" : "#9B9590",
                    }}
                  >
                    {input.hint}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* BADGES */}
        <div style={{ marginBottom: 64 }}>
          <div
            className="section-line"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#9B9590",
              fontWeight: 600,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            Badges
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            {[
              { label: "Default", bg: "#1B2B4B", color: "white" },
              { label: "● Active", bg: "#FDECE6", color: "#E8623A" },
              { label: "● Success", bg: "#E8F0EE", color: "#4A8075" },
              { label: "Archived", bg: "#E8E4DF", color: "#9B9590" },
              {
                label: "Draft",
                bg: "transparent",
                color: "#9B9590",
                border: "1.5px solid #E8E4DF",
              },
              { label: "React Native", bg: "#1B2B4B", color: "white" },
              { label: "TypeScript", bg: "#FDECE6", color: "#E8623A" },
              { label: "Figma", bg: "#E8F0EE", color: "#4A8075" },
            ].map((b) => (
              <span
                key={b.label}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 10px",
                  borderRadius: 100,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.03em",
                  background: b.bg,
                  color: b.color,
                  border: b.border || "none",
                }}
              >
                {b.label}
              </span>
            ))}
          </div>
        </div>

        {/* CARDS */}
        <div style={{ marginBottom: 64 }}>
          <div
            className="section-line"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#9B9590",
              fontWeight: 600,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            Cards
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
            }}
          >
            {[
              {
                title: "Default",
                body: "Clean white surface with a subtle border and hover lift.",
                bg: "white",
                color: "#1A1A1A",
                border: "1px solid #E8E4DF",
              },
              {
                title: "Filled",
                body: "Navy background for high-emphasis content blocks.",
                bg: "#1B2B4B",
                color: "white",
              },
              {
                title: "Accent",
                body: "Coral for calls to action and featured content.",
                bg: "#E8623A",
                color: "white",
              },
              {
                title: "Sage",
                body: "Soft green for success states and calm contexts.",
                bg: "#E8F0EE",
                color: "#1B2B4B",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="ds-card"
                style={{
                  background: card.bg,
                  border: card.border || "none",
                  padding: 24,
                }}
              >
                <div
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontSize: 16,
                    fontWeight: 700,
                    marginBottom: 8,
                    color: card.color,
                  }}
                >
                  {card.title}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: card.color,
                    opacity: 0.75,
                    lineHeight: 1.5,
                  }}
                >
                  {card.body}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SPACING */}
        <div style={{ marginBottom: 64 }}>
          <div
            className="section-line"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#9B9590",
              fontWeight: 600,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            Spacing Scale
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tokens.spacing.map((size) => (
              <div
                key={size}
                style={{ display: "flex", alignItems: "center", gap: 16 }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontFamily: "monospace",
                    color: "#9B9590",
                    width: 100,
                  }}
                >
                  {size}px / {size / 16}rem
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      height: 8,
                      width: size,
                      background: "#1B2B4B",
                      borderRadius: 4,
                      opacity: 0.8,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DesignSystem;
